import React, { forwardRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    selectImageLayout,
    selectImageProject,
    selectLoading,
} from '../../../../store/selectors/imageSelectors';
import s from './Canvas.module.css';
import Slot from '../slot/Slot';
import TextContainer from '../text-container/TextContainer';
import { useAppDispatch } from '../../../../store/store';
import { undo } from '../../../../store/slices/imageSlice';
import Loader from '../../../../shared/ui/loader/Loader';
import CanvasOverlayActions from '../../../custom-post-page/widgets/canvas-overlay-actions/CanvasOverlayActions';
import { selectVideo, selectVideoState } from '../../../../store/selectors/projectSelectors';
import VideoSlot from '../slot/VideoSlot';
import CropperOverlay from '../../../custom-post-page/widgets/canvas-overlay-actions/CropperOverlay';
import { selectActivePrompt } from '../../../../store/selectors/pageSelectors';
import { toggleActivePrompt } from '../../../../store/slices/pageSlice';
import { updateTransformation } from '../../../../shared/api/imageProjectApi';
import { Transformation } from '../../../../shared/types/imagesType';

const Canvas = forwardRef<HTMLDivElement, { setNewImageWithEffects: (file: File) => void }>(
    ({ setNewImageWithEffects }, ref) => {
        const dispatch = useAppDispatch();
        const imageLayout = useSelector(selectImageLayout);
        const imageProject = useSelector(selectImageProject);
        const projectVideo = useSelector(selectVideo);

        const videoState = useSelector(selectVideoState);
        const activePrompt = useSelector(selectActivePrompt);

        const [isCropping, setIsCropping] = React.useState(false);

        const handleStartCrop = () => {
            dispatch(toggleActivePrompt(null));
            setIsCropping(true);
        };

        useEffect(() => {
            if (activePrompt) setIsCropping(false);
        }, [activePrompt]);

        useEffect(() => {
            const handleKeyDown = (e: KeyboardEvent) => {
                const isCtrlOrCmd = e.ctrlKey || e.metaKey;
                if (!isCtrlOrCmd) return;

                const key = e.key.toLowerCase();
                if (key !== 'z') return;

                const target = e.target as HTMLElement | null;
                const tag = target?.tagName?.toLowerCase();
                const isEditable =
                    tag === 'input' || tag === 'textarea' || target?.isContentEditable;

                if (isEditable) return;

                e.preventDefault();
                dispatch(undo());
            };

            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }, [dispatch]);

        const imagesById = new Map(imageProject?.allImagesForLayouts.map((img) => [img.id, img]));

        if (!imageLayout) return null;

        const saveImage = (file: File) => {
            setNewImageWithEffects(file);
        };

        const imagesWithFullData = Array.from({ length: imageLayout.slot_count }).map((_, i) => {
            const imgMeta = (imageLayout.images ?? []).find((img) => img.meta.order === i + 1);
            if (!imgMeta) return null;
            const fullData = imagesById.get(imgMeta.id);
            if (!fullData) return null;
            return {
                ...fullData,
                meta: imgMeta.meta,
            };
        });

        return (
            <div
                ref={ref}
                className={s.canvas}
                style={{
                    width:
                        isCropping || !imagesWithFullData[0]?.transformation?.presets?.aspect_ratio
                            ? '600px'
                            : undefined,
                    height:
                        isCropping || !imagesWithFullData[0]?.transformation?.presets?.aspect_ratio
                            ? '65vh'
                            : undefined,
                    maxWidth: isCropping ? undefined : '600px',
                    maxHeight: isCropping ? undefined : '65vh',
                    aspectRatio: !isCropping
                        ? imagesWithFullData[0]?.transformation?.presets?.aspect_ratio?.replace(
                              ':',
                              ' / ',
                          ) || undefined
                        : undefined,
                }}
            >
                <CanvasOverlayActions onStartCrop={handleStartCrop} projectVideo={projectVideo} />

                {projectVideo?.id ? (
                    <VideoSlot url={projectVideo?.url} />
                ) : (
                    <Slot img={imagesWithFullData?.[0]} saveImage={saveImage} />
                )}
                <TextContainer />

                {(videoState?.isAttaching ||
                    videoState?.isUploading ||
                    videoState?.isRequesting) && <Loader />}

                {!activePrompt && isCropping && imagesWithFullData?.[0]?.id && (
                    <CropperOverlay
                        imageData={imagesWithFullData[0]}
                        onCancel={() => setIsCropping(false)}
                        onApply={(croppedBlob: Blob, transform: Transformation) => {
                            const file = new File([croppedBlob], 'cropped.png', {
                                type: 'image/png',
                            });
                            saveImage(file);
                            dispatch(
                                updateTransformation({
                                    imageId: imagesWithFullData?.[0]?.id ?? 'none',
                                    transformation: transform,
                                }),
                            ).unwrap();
                            setIsCropping(false);
                        }}
                    />
                )}
            </div>
        );
    },
);

export default Canvas;
